import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CurrentUser, AdminUserRole } from '@/types/user';

// Auth store state
interface AuthState {
  // Authentication state
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  hasFetchedUser: boolean;

  // Permission methods
  hasPermission: (permission: string) => boolean;
  hasRole: (role: AdminUserRole | AdminUserRole[]) => boolean;
  canAccessOrganization: (organization_id: string) => boolean;

  // Actions
  setUser: (user: CurrentUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setHasFetchedUser: (fetched: boolean) => void;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      hasFetchedUser: false,

      // Permission methods
      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions.includes(permission);
      },

      hasRole: (role: AdminUserRole | AdminUserRole[]) => {
        const { user } = get();
        if (!user) return false;
        const roles = Array.isArray(role) ? role : [role];
        return roles.some((r) => user.role.includes(r));
      },

      canAccessOrganization: (organization_id: string) => {
        const { user } = get();
        if (!user) return false;

        // Super admin can access all organizations
        if (user.role.includes('super_admin')) return true;

        // Venue managers and viewers can only access their own organization
        return user.organization?.id === organization_id;
      },

      // Actions
      setUser: (user) =>
        set((state) => ({
          ...state,
          user,
          isAuthenticated: !!user,
          error: null,
        })),

      setLoading: (isLoading) =>
        set((state) => ({
          ...state,
          isLoading,
        })),

      setError: (error) =>
        set((state) => ({
          ...state,
          error,
          isLoading: false,
        })),

      setHasFetchedUser: (hasFetchedUser) =>
        set((state) => ({
          ...state,
          hasFetchedUser,
        })),

      logout: () =>
        set(() => ({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          hasFetchedUser: false,
        })),

      clearError: () =>
        set((state) => ({
          ...state,
          error: null,
        })),
    }),
    {
      name: 'auth-store',
    }
  )
);
